import { useEffect, useRef } from 'react'
import type { MouseEvent } from 'react'
import { AmbientLayer } from './components/AmbientLayer'
import { Entrance } from './components/Entrance'
import { Header } from './components/Header'
import { InputBar } from './components/InputBar'
import { OrbZone } from './components/OrbZone'
import { Stage } from './components/Stage'
import { SummaryPanel } from './components/SummaryPanel'
import { createDriver } from './drivers'
import { registerVisitorSession } from './api/client'
import { store, useVoiceState } from './state/store'
import type { Driver } from './state/types'
import { trackPageView } from './utils/analytics'
import { notifyPhoneCall, notifyPhoneShow } from './utils/phoneShow'
import { voiceEvent } from './utils/voiceEvents'
import { startVisitBeacons } from './utils/visitBeacon'

export default function App() {
	const state = useVoiceState()
	const driverRef = useRef<Driver | null>(null)

	useEffect(() => {
		registerVisitorSession()
		// visit_js / visit_interact — a visit counts only when JavaScript ran (Direkt cleanup 2026-09-27).
		const stopVisitBeacons = startVisitBeacons()
		const frame = requestAnimationFrame(() => trackPageView('/'))
		return () => {
			stopVisitBeacons()
			cancelAnimationFrame(frame)
			driverRef.current?.dispose()
			driverRef.current = null
		}
	}, [])

	const busy = !!(state.card || state.options || state.price || state.success || state.draft)

	/* withMic=false is „Inkább írok": the same conversation, started without ever
	   touching getUserMedia — the driver already knows how to run typed-only. */
	const handleStart = (withMic: boolean) => {
		if (state.entranceLeaving) return
		store.set({ entranceLeaving: true })
		window.setTimeout(() => {
			store.set({ phase: 'conversation', entranceLeaving: false })
			const driver = (driverRef.current = createDriver())
			voiceEvent('voice_start')
			void driver.start(withMic)
		}, 350)
	}

	/* The number exists nowhere until the first tap: tap 1 = Phone_Show (and the
	   driver may answer with the draft-code card), tap 2+ = the actual dial. */
	const handleCall = (e: MouseEvent<HTMLAnchorElement>) => {
		const s = store.get()
		if (!s.phoneShown) {
			e.preventDefault()
			store.set({ phoneShown: true })
			notifyPhoneShow()
			if (s.phase === 'conversation' && !s.done) {
				voiceEvent('voice_handoff')
				driverRef.current?.callTapped()
			}
			return
		}
		notifyPhoneCall()
	}

	const handleReceiptCall = () => {
		store.set({ phoneShown: true })
		notifyPhoneCall()
	}

	return (
		<div className="am-screen" data-orb={state.orb} data-phase={state.phase}>
			<AmbientLayer />
			<Header phoneShown={state.phoneShown} onCall={handleCall} />
			{/* The contract panel is the record of what was agreed — on the landing
			    there is nothing to record yet, and „0/6 pont rögzítve" reads as a
			    six-field form, which is the opposite of the promise below it. */}
			{state.phase === 'conversation' && (
				<SummaryPanel
					state={state}
					onToggle={() => store.set({ expanded: !state.expanded })}
					onEditRow={(key, label) => driverRef.current?.editRow(key, label)}
				/>
			)}
			{state.phase === 'entrance' ? (
				<Entrance
					leaving={state.entranceLeaving}
					phoneShown={state.phoneShown}
					onStart={handleStart}
					onCall={handleCall}
				/>
			) : (
				<>
					<OrbZone state={state} busy={busy} onOrbTap={() => driverRef.current?.toggleMic()} />
					<Stage
						state={state}
						onChip={(c) => driverRef.current?.pickChip(c)}
						onOption={(o) => driverRef.current?.pickOption(o)}
						onReceiptCall={handleReceiptCall}
					/>
				</>
			)}
			{state.phase === 'conversation' && (
				<InputBar
					listening={state.listening}
					onMicTap={() => driverRef.current?.toggleMic()}
					onSubmit={(text) => driverRef.current?.submitText(text)}
				/>
			)}
		</div>
	)
}
